import { Button, Divider, Stack } from '@mui/material';
import { FormEventHandler, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  AbgButton,
  AbgForm,
  AbgTypography,
  TextFormControl,
  useTranslation,
} from '@abg/kit-base';

import { GlobalLink } from '../../../GlobalLink';

import { dividerStyles, textLinkButtonStyles } from './LoginInputView.styling';
import { LoginInputViewFormData, LoginInputViewProps } from './types';

export const LoginInputView = ({
  model,
  inputLabel,
  confirmBtnText,
  rejectBtnText,
  primaryLinkText,
  secondaryLinkText,
  secondaryLinkHref,
  secondaryLinkTarget,
  subDescriptionText,
  testIdPrefix = 'login-input-view',
  onSubmit,
  onReject,
  onPrimaryLinkClick,
  onSecondaryLinkClick,
  hideCounter,
  isPassword,
  isLoading,
}: LoginInputViewProps) => {
  const t = useTranslation(model.i18n);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Two-layer disable guard:
  // - `isLoading` (external): parent already knows an API call is in-flight (e.g. from useLoginJourney).
  // - `isSubmitting` (internal): set the instant a button is clicked, before React re-renders with
  //   the external flag. This closes the gap where a user can spam-click faster than state propagates.
  // Either one being true locks every interactive element in this form.
  const disabled = isLoading || isSubmitting;

  const formMethods = useForm<LoginInputViewFormData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      inputValue: '',
    },
  });

  const { handleSubmit } = formMethods;

  const showRejectButton = hideCounter && !!rejectBtnText;
  const showSubDescription = !hideCounter;
  const showPrimaryLink = !!primaryLinkText && !!onPrimaryLinkClick;
  const showSecondaryLinkButton = !!secondaryLinkText && !!onSecondaryLinkClick;
  const showSecondaryLinkHref = !!secondaryLinkText && !!secondaryLinkHref;
  const showSecondaryLink = showSecondaryLinkButton || showSecondaryLinkHref;
  const showLinksDivider = showPrimaryLink && showSecondaryLink;

  // Handles the "Continue" / confirm button (form submit).
  // Disables all buttons immediately, delegates to the parent's onSubmit (which fires an API call),
  // and only re-enables + resets the form once the API responds. Without this, users can click
  // "Continue" 5 times in 200ms and fire 5 parallel Mosaic requests — all of which fail or conflict.
  const onFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    (event) => {
      if (!onSubmit) return;

      void handleSubmit((data) => {
        setIsSubmitting(true);

        // IIFE is required because `onClick`/`onSubmit` expects `() => void`, not `() => Promise<void>`.
        // Making the useCallback itself async triggers @typescript-eslint/no-misused-promises.
        void (async () => {
          try {
            await onSubmit(data);
          } finally {
            setIsSubmitting(false);
            formMethods.reset();
          }
        })();
      })(event);
    },
    [formMethods, handleSubmit, onSubmit],
  );

  // Handles the "Resend" button. Same disable pattern as submit.
  // Always resets the form regardless of whether onReject is provided — the reject button
  // is a UI reset action (clear input), and the optional callback is a bonus (trigger resend API).
  const onFormReject = useCallback(() => {
    setIsSubmitting(true);

    // IIFE: can't make useCallback async — would violate @typescript-eslint/no-misused-promises on onClick.
    void (async () => {
      try {
        await onReject?.();
      } finally {
        setIsSubmitting(false);
        formMethods.reset();
      }
    })();
  }, [onReject, formMethods]);

  // Handles "Receive code via email/phone" or "Log in with code" link.
  // These trigger async API calls (handleOtherOptionsOtp, handleSwitchToOtp) that switch the
  // delivery method or login mode. Without disabling, a rapid double-click sends two "switch"
  // requests — the second one hits an invalid journey state and errors out.
  const onPrimaryLinkAction = useCallback(() => {
    if (!onPrimaryLinkClick) return;

    setIsSubmitting(true);

    // IIFE: can't make useCallback async — would violate @typescript-eslint/no-misused-promises on onClick.
    void (async () => {
      try {
        await onPrimaryLinkClick();
      } finally {
        setIsSubmitting(false);
      }
    })();
  }, [onPrimaryLinkClick]);

  // Handles "Log in with password" link. While handleSwitchToPassword itself is synchronous
  // (just sets local state), we still disable during the action to prevent race conditions
  // if the user clicks both "switch to password" and "submit OTP" within the same event loop tick.
  const onSecondaryLinkAction = useCallback(() => {
    if (!onSecondaryLinkClick) return;

    setIsSubmitting(true);

    // IIFE: can't make useCallback async — would violate @typescript-eslint/no-misused-promises on onClick.
    void (async () => {
      try {
        await onSecondaryLinkClick();
      } finally {
        setIsSubmitting(false);
      }
    })();
  }, [onSecondaryLinkClick]);

  return (
    <FormProvider {...formMethods}>
      <AbgForm data-testid={`${testIdPrefix}-form`} onSubmit={onFormSubmit}>
        <Stack direction="column" rowGap={2}>
          <TextFormControl
            name="inputValue"
            type={isPassword ? 'password' : 'text'}
            label={inputLabel}
            validationRules={{
              required: t('global.field.validation.fieldRequired.message', {
                field: inputLabel,
                interpolation: { escapeValue: false },
              }),
            }}
            textFieldProps={{
              fullWidth: true,
              disabled,
            }}
            testIdPrefix={testIdPrefix}
            testId="input"
          />

          <Stack rowGap={2}>
            <AbgButton
              type="submit"
              className="primary"
              disabled={disabled}
              data-testid={`${testIdPrefix}-confirm-btn`}
            >
              <AbgTypography component="span" variant="actionSmallRegular">
                {confirmBtnText}
              </AbgTypography>
            </AbgButton>

            {showRejectButton && (
              <AbgButton
                onClick={onFormReject}
                className="tertiary"
                disabled={disabled}
                data-testid={`${testIdPrefix}-reject-btn`}
              >
                <AbgTypography component="span" variant="actionSmallRegular">
                  {rejectBtnText}
                </AbgTypography>
              </AbgButton>
            )}
          </Stack>

          {showSubDescription && (
            <Stack justifyContent="center" alignItems="center">
              <AbgTypography
                component="span"
                variant="actionSmallRegular"
                htmlContent={subDescriptionText}
                data-testid={`${testIdPrefix}-sub-description`}
              />
            </Stack>
          )}

          <Stack direction="row" columnGap={2} justifyContent="center">
            {showPrimaryLink && (
              <Button
                variant="text"
                onClick={onPrimaryLinkAction}
                disabled={disabled}
                sx={textLinkButtonStyles}
                data-testid={`${testIdPrefix}-primary-link-btn`}
              >
                <AbgTypography component="span" variant="bodySmallRegular">
                  {primaryLinkText}
                </AbgTypography>
              </Button>
            )}

            {showLinksDivider && (
              <Divider orientation="vertical" flexItem sx={dividerStyles} />
            )}

            {showSecondaryLinkButton && (
              <Button
                variant="text"
                onClick={onSecondaryLinkAction}
                disabled={disabled}
                sx={textLinkButtonStyles}
                data-testid={`${testIdPrefix}-secondary-link-btn`}
              >
                <AbgTypography component="span" variant="bodySmallRegular">
                  {secondaryLinkText}
                </AbgTypography>
              </Button>
            )}

            {showSecondaryLinkHref && (
              <GlobalLink
                href={secondaryLinkHref}
                target={secondaryLinkTarget || '_self'}
                variant="text"
                sx={textLinkButtonStyles}
                data-testid={`${testIdPrefix}-secondary-link`}
              >
                <AbgTypography component="span" variant="bodySmallRegular">
                  {secondaryLinkText}
                </AbgTypography>
              </GlobalLink>
            )}
          </Stack>
        </Stack>
      </AbgForm>
    </FormProvider>
  );
};
